/** Кто может решить по закупке на стадии согласования.
 *
 * Правило одно на всю систему и повторяет серверное
 * (`_ensure_may_decide` в `backend/app/api/v1/processes.py`):
 *
 *  - согласующий и админ решают по любой закупке;
 *  - закупщик — по своей: той, которую взял в работу или завёл сам;
 *  - закупка без ведущего (её сконвертировала система) открыта любому
 *    закупщику — назначить ведущего некому, иначе она зависнет навсегда.
 *
 * Держать это в одном месте важно: правило считают и кнопки на странице
 * закупки, и очередь согласований, и счётчик в левом меню. Разойдись они —
 * в меню будет цифра, за которой ничего нельзя сделать.
 */
import { useAppSettings } from "../api/hooks";
import { hasRole } from "../auth/AuthProvider";
import type { AuthUser } from "../auth/AuthProvider";

interface DecidableProcess {
  owner_purchaser_id?: string | null;
}

export function canDecideProcurement(
  user: AuthUser | null,
  process: DecidableProcess,
  /** Группа согласования закупок из админки — тогда решают её участники. */
  groupId?: string | null,
): boolean {
  if (groupId) {
    // Замещающих сервер пропустит и так; здесь — участники и админ.
    return user?.role === "admin" || Boolean(user?.approval_group_ids?.includes(groupId));
  }
  if (!hasRole(user, "purchaser", "approver", "admin")) return false;
  if (user?.role !== "purchaser") return true;
  return !process.owner_purchaser_id || process.owner_purchaser_id === user.id;
}

/** Группа согласования закупок (настройка на странице групп) — или null. */
export function useProcurementGroupId(): string | null {
  const settings = useAppSettings();
  return settings.data?.find((s) => s.key === "procurement_approval_group")?.value?.trim() || null;
}
